import React, { useEffect, useState } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { NotificationService } from '../services/notification.service';
import { useAuth } from '../contexts/AuthContext';

/**
 * Composant qui gère l'affichage des notifications push
 * lorsque l'application est au premier plan
 */
const NotificationHandler: React.FC = () => {
  const [notification, setNotification] = useState<{
    open: boolean;
    title: string;
    body: string;
    url?: string;
  }>({
    open: false,
    title: '',
    body: '',
  });
  
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Ne configurer les notifications que si l'utilisateur est connecté
    if (!user) return;

    let unsubscribe = () => {};
    
    try {
      // Vérifier si le service est disponible avant d'essayer de l'utiliser
      // Écouter les messages entrants lorsque l'application est au premier plan
      unsubscribe = NotificationService.onForegroundMessage((payload) => {
        // Extraire les informations de la notification
        const notificationTitle = payload.notification?.title || 'Nouvelle notification';
        const notificationBody = payload.notification?.body || '';
        const notificationUrl = payload.data?.url || '';

        // Afficher la notification
        setNotification({
          open: true,
          title: notificationTitle,
          body: notificationBody,
          url: notificationUrl
        });
      });
    } catch (error) {
      // Ignorer silencieusement les erreurs de Firebase Messaging
      // Cela permet à l'application de fonctionner même si les notifications ne sont pas prises en charge
      console.log('Notifications non disponibles sur ce navigateur');
    }

    // Nettoyer l'écouteur lors du démontage du composant
    return () => unsubscribe();
  }, [user, navigate]);

  // Gérer la fermeture de la notification
  const handleClose = () => {
    setNotification({ ...notification, open: false });
  };

  // Gérer le clic sur la notification
  const handleClick = () => {
    handleClose();
    if (notification.url) {
      navigate(notification.url);
    }
  };

  return (
    <Snackbar
      open={notification.open}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert 
        onClose={handleClose} 
        severity="info" 
        sx={{ width: '100%' }}
        action={
          notification.url ? (
            <Button color="inherit" size="small" onClick={handleClick}>
              Voir
            </Button>
          ) : undefined
        }
      >
        <strong>{notification.title}</strong>
        <p style={{ margin: '4px 0 0 0' }}>{notification.body}</p>
      </Alert>
    </Snackbar>
  );
};

export default NotificationHandler;
